/**
 * Alert Rules Configuration Page (Admin)
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  FormControlLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { AlertRule, AlertRuleCreate, AlertType, AlertSeverity } from '../../types/alerts';

const AlertRulesConfig: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [notification, setNotification] = useState<{message: string; severity: 'success' | 'error' | 'warning'} | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<Partial<AlertRuleCreate>>({
    rule_name: '',
    alert_type: AlertType.STOCK_BELOW_MIN,
    severity: AlertSeverity.WARNING,
    conditions: { threshold: 3 },
    notification_channels: ['in_app'],
    is_active: true,
    evaluation_frequency: 'hourly',
    suppression_window: 60,
  });

  // Fetch alert rules
  const { data: rules, isLoading } = useQuery<AlertRule[]>({
    queryKey: ['alertRules'],
    queryFn: async () => {
      const response = await api.getAlertRules();
      return response.data;
    },
  });

  // Create rule mutation
  const createMutation = useMutation({
    mutationFn: (data: AlertRuleCreate) => api.createAlertRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertRules'] });
      handleCloseDialog();
      setNotification({ message: 'Alert rule created successfully!', severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({ message: `Error creating rule: ${error.response?.data?.detail || error.message}`, severity: 'error' });
    },
  });

  // Update rule mutation
  const updateMutation = useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: any }) =>
      api.updateAlertRule(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertRules'] });
      handleCloseDialog();
      setNotification({ message: 'Alert rule updated successfully!', severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({ message: `Error updating rule: ${error.response?.data?.detail || error.message}`, severity: 'error' });
    },
  });

  // Delete rule mutation
  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) => api.deleteAlertRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertRules'] });
      setDeleteConfirmOpen(false);
      setRuleToDelete(null);
      setNotification({ message: 'Alert rule deleted successfully!', severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({ message: `Error deleting rule: ${error.response?.data?.detail || error.message}`, severity: 'error' });
    },
  });

  const handleOpenDialog = (rule?: AlertRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        rule_name: rule.rule_name,
        alert_type: rule.alert_type,
        severity: rule.severity,
        conditions: rule.conditions,
        is_active: rule.is_active,
        evaluation_frequency: rule.evaluation_frequency,
      });
    } else {
      setEditingRule(null);
      setFormData({
        rule_name: '',
        alert_type: AlertType.STOCK_BELOW_MIN,
        severity: AlertSeverity.WARNING,
        conditions: { threshold: 3 },
        notification_channels: ['in_app'],
        is_active: true,
        evaluation_frequency: 'hourly',
        suppression_window: 60,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
  };

  const handleSaveRule = () => {
    if (!formData.rule_name || !formData.alert_type) {
      setFormError('Please fill in all required fields');
      return;
    }
    setFormError(null);

    if (editingRule) {
      // Update existing rule
      updateMutation.mutate({
        ruleId: editingRule.rule_id,
        data: {
          rule_name: formData.rule_name,
          severity: formData.severity,
          conditions: formData.conditions,
          is_active: formData.is_active,
          evaluation_frequency: formData.evaluation_frequency,
        },
      });
    } else {
      // Create new rule
      createMutation.mutate(formData as AlertRuleCreate);
    }
  };

  const handleDeleteRule = (ruleId: string) => {
    setRuleToDelete(ruleId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (ruleToDelete) {
      deleteMutation.mutate(ruleToDelete);
    }
  };

  const handleConditionChange = (key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [key]: value,
      },
    }));
  };

  const getSeverityBadgeClass = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return 'status-badge status-badge--danger';
      case AlertSeverity.WARNING:
        return 'status-badge status-badge--warning';
      case AlertSeverity.INFO:
        return 'status-badge status-badge--neutral';
      default:
        return 'status-badge status-badge--neutral';
    }
  };

  const getAlertTypeLabel = (type: AlertType) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Box>
      {/* Page title */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '20px' }}>
        <Box>
          <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Alert Rules Configuration
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
            Manage alert rules that monitor your inventory and trigger notifications
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/alerts')}>
            Back to Alerts
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Create Rule
          </Button>
        </Box>
      </Box>

      {/* Rules Table */}
      <Box className="page-enter">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rule Status</TableCell>
                <TableCell>Rule Name</TableCell>
                <TableCell>Alert Type</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Conditions</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : rules && rules.length > 0 ? (
                rules.map((rule) => (
                  <TableRow key={rule.rule_id} hover>
                    <TableCell>
                      <span className={rule.is_active ? 'status-badge status-badge--success' : 'status-badge status-badge--neutral'}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                        {rule.rule_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                        {getAlertTypeLabel(rule.alert_type)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <span className={getSeverityBadgeClass(rule.severity)}>
                        {rule.severity.toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>
                        {JSON.stringify(rule.conditions)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                        {rule.evaluation_frequency}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenDialog(rule)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRule(rule.rule_id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 3 }}>
                      <SettingsIcon sx={{ fontSize: 48, color: 'var(--color-text-muted)', mb: 1 }} />
                      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                        No alert rules configured. Create your first rule to get started.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Rule Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rule Name"
                value={formData.rule_name}
                onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                required
              />
            </Grid>

            {/* Alert Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Alert Type</InputLabel>
                <Select
                  value={formData.alert_type}
                  label="Alert Type"
                  onChange={(e) =>
                    setFormData({ ...formData, alert_type: e.target.value as AlertType })
                  }
                  disabled={!!editingRule} // Can't change type when editing
                >
                  <MenuItem value={AlertType.STOCK_BELOW_MIN}>Stock Below Minimum</MenuItem>
                  <MenuItem value={AlertType.STOCKOUT}>Stockout</MenuItem>
                  <MenuItem value={AlertType.ORDER_PENDING}>Order Pending</MenuItem>
                  <MenuItem value={AlertType.EXCESS_STOCK}>Excess Stock</MenuItem>
                  <MenuItem value={AlertType.FORECAST_ANOMALY}>Forecast Anomaly</MenuItem>
                  <MenuItem value={AlertType.EXPIRY_WARNING}>Expiry Warning</MenuItem>
                  <MenuItem value={AlertType.SLOW_MOVING}>Slow Moving</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Severity */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={formData.severity}
                  label="Severity"
                  onChange={(e) =>
                    setFormData({ ...formData, severity: e.target.value as AlertSeverity })
                  }
                >
                  <MenuItem value={AlertSeverity.INFO}>Info</MenuItem>
                  <MenuItem value={AlertSeverity.WARNING}>Warning</MenuItem>
                  <MenuItem value={AlertSeverity.CRITICAL}>Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Conditions - Dynamic based on alert type */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', mb: 1 }}>
                Conditions
              </Typography>
              {formData.alert_type === AlertType.STOCK_BELOW_MIN && (
                <TextField
                  fullWidth
                  type="number"
                  label="Days of Supply Threshold"
                  value={formData.conditions?.threshold || 3}
                  onChange={(e) => handleConditionChange('threshold', parseFloat(e.target.value))}
                  helperText="Alert when days of supply falls below this value"
                />
              )}
              {formData.alert_type === AlertType.ORDER_PENDING && (
                <TextField
                  fullWidth
                  type="number"
                  label="Pending Hours Threshold"
                  value={formData.conditions?.pending_hours || 24}
                  onChange={(e) => handleConditionChange('pending_hours', parseInt(e.target.value))}
                  helperText="Alert when order is pending for more than this many hours"
                />
              )}
              {formData.alert_type === AlertType.EXCESS_STOCK && (
                <TextField
                  fullWidth
                  type="number"
                  label="Days of Supply Threshold"
                  value={formData.conditions?.days_of_supply_threshold || 60}
                  onChange={(e) =>
                    handleConditionChange('days_of_supply_threshold', parseInt(e.target.value))
                  }
                  helperText="Alert when days of supply exceeds this value"
                />
              )}
              {formData.alert_type === AlertType.STOCKOUT && (
                <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                  No conditions required - alerts on zero stock
                </Typography>
              )}
            </Grid>

            {/* Evaluation Frequency */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Evaluation Frequency</InputLabel>
                <Select
                  value={formData.evaluation_frequency}
                  label="Evaluation Frequency"
                  onChange={(e) =>
                    setFormData({ ...formData, evaluation_frequency: e.target.value })
                  }
                >
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Suppression Window (only for new rules) */}
            {!editingRule && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Suppression Window (minutes)"
                  value={formData.suppression_window}
                  onChange={(e) =>
                    setFormData({ ...formData, suppression_window: parseInt(e.target.value) })
                  }
                  helperText="Prevent duplicate alerts within this time"
                />
              </Grid>
            )}

            {/* Active Status */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Rule is Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveRule}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Alert Rule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this alert rule? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity || 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertRulesConfig;
